/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import * as icons from '@ant-design/icons';
import {
  ApolloCache,
  NormalizedCacheObject,
  useMutation,
  useQuery,
} from '@apollo/client';
import {
  checkPermission,
  GET_IDEA,
  GET_ME,
  Idea,
  IDEA_LIKE,
  IDEA_UNLIKE,
  Me,
  PERMISSIONS,
} from '@frontend/data-access';
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { DiscussionWall } from '@frontend/feature-discussion';
import { formatDate } from '@frontend/shared/util';
import {
  Button,
  Col,
  Divider,
  Dropdown,
  Empty,
  Image,
  Modal,
  Row,
  Spin,
  Tabs,
  Typography,
} from 'antd';
import IdeaQuestionnaire from 'libs/feature-idea-channel/src/lib/idea-channel-detail/answers';
import IdeaDetailMetrics from 'libs/feature-idea-channel/src/lib/idea-channel-detail/metrics';
import getConfig from 'next/config';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useState } from 'react';

import EvaluateScore from '../evaluate-score/evaluate-score';
import IdeaContribute from '../idea-contribute/idea-contribute';
import IdeaContributionList from '../idea-contribution-list/idea-contribution-list';
import IdeaDetailBasics from '../idea-detail-basics/idea-detail-basics';
import IdeaEvaluationResultTag from '../idea-evaluation-result-tag/idea-evaluation-result-tag';
import IdeaReport from '../idea-report/idea-report';
import ReviewerSuggestionModal from '../reviewer-suggestion-modal/ReviewerSuggestionModal';
import UnsplashImage from '../unsplash-image/unsplash-image';
import IdeaDetailAISuggestion from './ai-suggestion';
import IdeaDetailTeamInvite from './invite-alert';
import IdeaDetailInviteChallenge from './invite-challenge';

type Props = {
  ideaId?: string;
  mode?: 'standart' | 'modal';
};

export function IdeaDetail({ ideaId, mode = 'standart' }: Props) {
  const { publicRuntimeConfig } = getConfig();
  const { t: tcommon } = useTranslation('common');
  const router = useRouter();
  const id = ideaId ?? (router.query['id'] as string);
  const [contributeOpen, setContributeOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const { data, loading } = useQuery<{ idea: Idea }>(GET_IDEA, {
    variables: { ideaId: id },
    skip: !id,
  });

  const handleUpdateCacheLike = (cache: ApolloCache<NormalizedCacheObject>) => {
    const cacheData: { idea: Idea } | null = cache.readQuery({
      query: GET_IDEA,
      variables: {
        ideaId: id,
      },
    });

    if (cacheData?.idea) {
      cache.writeQuery({
        query: GET_IDEA,
        variables: {
          ideaId: id,
        },
        data: {
          idea: {
            ...cacheData?.idea,
            isLiked: true,
            likes: cacheData?.idea.likes + 1,
          },
        },
      });
    }
  };

  const handleUpdateCacheUnLike = (
    cache: ApolloCache<NormalizedCacheObject>
  ) => {
    const cacheData: { idea: Idea } | null = cache.readQuery({
      query: GET_IDEA,
      variables: {
        ideaId: id,
      },
    });

    if (cacheData?.idea) {
      cache.writeQuery({
        query: GET_IDEA,
        variables: {
          ideaId: id,
        },
        data: {
          idea: {
            ...cacheData?.idea,
            isLiked: false,
            likes: cacheData?.idea.likes - 1,
          },
        },
      });
    }
  };

  const handleReport = () => {
    setReportOpen(!reportOpen);
  };

  const handleContribute = () => {
    setContributeOpen(!contributeOpen);
  };

  const [likeIdea] = useMutation(IDEA_LIKE, {
    update: handleUpdateCacheLike,
  });
  const [unlikeIdea] = useMutation(IDEA_UNLIKE, {
    update: handleUpdateCacheUnLike,
  });

  const items = [];

  if (
    meData?.me?.role &&
    data?.idea?.challenge &&
    (meData?.me?.role === 'ADMIN' ||
      checkPermission(meData?.me, [
        PERMISSIONS.INTERNAL_CHALLENGE_MANAGEMENT,
      ])) &&
    data?.idea?.challenge?.experts?.filter((ex) => ex.id === meData?.me?.id)
      .length
  ) {
    if (data?.idea?.internalDiscussion?.id) {
      items.push({
        key: '0',
        label:
          data?.idea?.internalComments === 0
            ? tcommon('idea_detail_evaluation_comments')
            : `${tcommon('idea_detail_evaluation_comments')} (${
                data?.idea.internalComments
              })`,
        children: (
          <>
            <div className="text-xs">
              {tcommon('idea_detail_evaluation_comment_description')}
            </div>
            <DiscussionWall id={data?.idea?.internalDiscussion?.id} title=" " />
          </>
        ),
      });
    }
  }
  if (data?.idea?.discussion?.id) {
    items.push({
      key: '1',
      label:
        data?.idea.comments === 0
          ? tcommon('idea_detail_comments')
          : `${tcommon('idea_detail_comments')} (${data?.idea.comments})`,
      children: <DiscussionWall id={data?.idea?.discussion?.id} title=" " />,
    });
  }
  if (data?.idea?.regContribute) {
    items.push({
      key: '2',
      label: (
        <div data-test="idea-contribute-tab">
          {data?.idea.contributeCount === 0
            ? tcommon('idea_detail_contributions')
            : `${tcommon('idea_detail_contributions')} (${
                data?.idea.contributeCount
              })`}
        </div>
      ),
      children: (
        <IdeaContributionList
          title=" "
          ideaId={data?.idea.id}
          contributionCount={data?.idea.contributeCount}
        />
      ),
    });
  }

  const [isReviewerModalOpen, setIsReviewerModalOpen] = useState(false);

  const handleSuggestReviewers = () => {
    setIsReviewerModalOpen(true);
  };

  const handleLike = () => {
    if (data?.idea.isLiked) {
      unlikeIdea({
        variables: {
          id: id,
        },
      });
    } else {
      likeIdea({
        variables: {
          id: id,
        },
      });
    }
  };

  if (loading)
    return (
      <div className="text-center">
        <Spin />
      </div>
    );

  if (!data?.idea) {
    return (
      <div className="text-center p-5">
        <Empty description={tcommon('idea_detail_not_found')} />
      </div>
    );
  }

  return (
    <div className="relative space-y-4">
      {mode === 'standart' && (
        <Row className="mb-2">
          <Button type="link" onClick={() => router.back()} className="!p-0">
            <icons.ArrowLeftOutlined />
            {tcommon('back')}
          </Button>
        </Row>
      )}
      {meData?.me?.id === data?.idea?.createdBy?.id && (
        <IdeaDetailInviteChallenge idea={data?.idea} />
      )}
      <IdeaDetailTeamInvite idea={data?.idea} />
      <div className="flex items-center flex-wrap justify-between gap-2">
        <div className="flex items-center flex-wrap gap-4">
          <div>
            <div className="font-semibold">{tcommon('idea_detail_date')}</div>
            <div className="text-gray-700 text-sm">
              {formatDate(data?.idea?.createdAt, 'DD MMM YYYY, HH:mm')}
            </div>
          </div>
          {meData?.me?.role === 'ADMIN' ||
          ((checkPermission(meData?.me, [
            PERMISSIONS.INTERNAL_CHALLENGE_MANAGEMENT,
          ]) ||
            meData?.me?.id === data?.idea?.createdBy?.id ||
            (data?.idea?.challenge !== undefined &&
              data?.idea?.challenge?.experts?.filter(
                (ex) => ex.id === meData?.me?.id
              ).length)) &&
            data?.idea.criteriaResults.length > 0) ? (
            <div>
              <div className="font-semibold">
                {tcommon('idea_detail_average_expert_score')}
              </div>
              <div className="text-gray-700 text-sm">
                <IdeaEvaluationResultTag ideaId={id} />
              </div>
            </div>
          ) : null}
        </div>
        <div className="flex items-center md:justify-end gap-6">
          <div className="flex justify-between items-center gap-3 flex-wrap">
            {data?.idea.regContribute && (
              <Button
                shape="round"
                type="primary"
                onClick={() => handleContribute()}
                data-test="idea-contribute-btn"
              >
                {tcommon('idea_detail_contribute')}
              </Button>
            )}
            <Button
              shape="round"
              onClick={handleSuggestReviewers}
              data-test="idea-suggest-reviewer-btn"
            >
              {tcommon('idea_detail_suggest_reviewers')}
            </Button>
            <div
              onClick={handleLike}
              className={`px-2 py-1 rounded-full border border-gray-400 flex flex-col gap-0 items-center justify-center select-none cursor-pointer ${
                data?.idea.isLiked
                  ? 'bg-primary-600 text-white'
                  : 'hover:border-primary-600 hover:text-primary-600'
              }`}
              data-test="idea-like-btn"
            >
              <icons.CaretUpOutlined />
              <span className="text-xs">{data?.idea.likes}</span>
            </div>
            <Dropdown
              menu={{
                items: [
                  {
                    key: '1',
                    disabled: data?.idea.isReported,
                    label: (
                      <span
                        data-test="idea-report-btn"
                        onClick={() => {
                          !data?.idea.isReported && handleReport();
                        }}
                      >
                        {tcommon('idea_detail_report')}
                      </span>
                    ),
                  },
                ],
              }}
              placement="bottomRight"
            >
              <Button
                shape="circle"
                icon={<icons.EllipsisOutlined />}
                data-test="idea-dropdown-btn"
              />
            </Dropdown>
          </div>
        </div>
      </div>
      <Row gutter={[16, 16]}>
        <Col span={24} md={14}>
          <div className="space-y-6">
            <div className="border p-3 rounded-xl border-gray-400 overflow-hidden">
              <div className="xl:float-left text-center">
                {data?.idea?.coverPhoto && (
                  <div className="mr-3">
                    <Image
                      className="rounded-xl overflow-hidden"
                      alt={data?.idea.name}
                      width={180}
                      height={180}
                      preview={false}
                      src={data?.idea?.coverPhoto.url}
                    />
                  </div>
                )}
                {data?.idea?.coverPhotoUrl && (
                  <UnsplashImage
                    image={data?.idea.coverPhotoUrl}
                    name={data?.idea.name}
                  />
                )}
                {!data?.idea?.coverPhoto && !data?.idea?.coverPhotoUrl && (
                  <div className="mr-3">
                    <Image
                      className={`rounded-xl overflow-hidden`}
                      alt={data?.idea.name}
                      width={180}
                      height={180}
                      preview={false}
                      src={`${publicRuntimeConfig.staticFolder}/data/idea_placeholder_small.jpeg`}
                    />
                  </div>
                )}
              </div>
              <Typography.Title level={4} data-test="idea-title">
                {data?.idea.name}
              </Typography.Title>
              <p
                className="text-wrap break-words whitespace-break-spaces"
                data-test="idea-problem"
              >
                {data?.idea.problem}
              </p>
            </div>

            {data?.idea?.applicants.find(
              (item) =>
                (item.user.id === meData?.me.id &&
                  item.status === 'APPROVED') ||
                data?.idea?.createdBy?.id === meData?.me?.id
            ) && <IdeaQuestionnaire idea={data.idea} printMode={false} />}
            {meData?.me?.id === data?.idea?.createdBy?.id && (
              <IdeaDetailAISuggestion ideaId={id} />
            )}
            <Divider className="!my-0" />
            <IdeaDetailMetrics idea={data?.idea} />
          </div>
          {mode === 'modal' && data && (
            <EvaluateScore type="IDEA" ideaId={id} />
          )}
          <Tabs items={items} />
        </Col>
        <Col span={24} md={10}>
          <IdeaDetailBasics data={data?.idea} />
        </Col>
      </Row>
      <Modal
        title={tcommon('idea_detail_contribute')}
        open={contributeOpen}
        onCancel={() => handleContribute()}
        footer={null}
      >
        <IdeaContribute data={data?.idea} onCancel={() => handleContribute()} />
      </Modal>
      <Modal
        title={tcommon('idea_detail_report_title')}
        open={reportOpen}
        onCancel={() => handleReport()}
        footer={null}
      >
        <IdeaReport data={data?.idea} onCancel={() => handleReport()} />
      </Modal>
      <ReviewerSuggestionModal
        visible={isReviewerModalOpen}
        onClose={() => setIsReviewerModalOpen(false)}
      />
    </div>
  );
}

export default IdeaDetail;
